import { TranslateService } from '@ngx-translate/core';
import { Component, DestroyRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormControl, ValidationErrors, Validators } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { Organization } from 'src/app/core/model/organization';
import { OrganizationService } from 'src/app/core/service/organization.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { UserService } from 'src/app/core/service/user.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { TextProcessor } from 'src/app/core/utility/text.processor';
import { OrganizationValidators } from 'src/app/core/validators/organization.validators';
import { User } from 'src/app/user/model/user';
import { Extension } from '../model/extension';
import { groupBy, GroupResult } from '@progress/kendo-data-query';
import { sortBy } from 'lodash';
import { ChannelService } from 'src/app/core/service/channel.service';
import { TabStripComponent } from '@progress/kendo-angular-layout';
import { ExtensionService } from 'src/app/core/service/extension.service';
import { DictionaryService } from 'src/app/core/service/dictionary.service';

@Component({
  standalone: false,
  selector: 'app-extension-edit',
  templateUrl: './extension-edit.component.html',
  styleUrls: ['./extension-edit.component.scss']
})
export class ExtensionEditComponent implements OnInit, OnDestroy
{
  private static readonly CONFIGURABLE_DATA_TYPE_CATEGORIES = ['COMMUNITY_SERVICE', 'ORGANIZATION'];

  @ViewChild('tabstrip') public tabstrip: TabStripComponent;

  @Input() public isNew = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<Extension> = new EventEmitter();

  public editForm: any;
  public editFormActive = false;
  public organizations = [];
  public model = {};
  public fields: FormlyFieldConfig[] = [];
  public patientLabel = {};
  public inValueChanges = false;
  public searchTerms = '';
  public allSelectableChannels = [];
  public channelsFiltered: Organization[] = [];
  public virtualChannel: any = { itemHeight: 30 };
  public noChannelSelected: string;
  public groupedChannels: GroupResult[] = [];
  public extensionCategoryCannotBeChanged: string;
  public extensionDataTypes: Array<{ text: string; value: string }> = [];
  public readonly extensionValueDbMaxLength = ExtensionService.EXTENSION_VALUE_DB_MAX_LENGTH;
  public codeSets: any[] = [];
  public dataTypeFieldsLocked = false;

  /**
   * Current logged in user
   */
  private currentUser: User;
  private currentUserOrganization: Organization;
  private loadedExtension: Extension;
  private initializedExtensionId: number | null = null;
  private dataTypeInUseCheckKey: string | null = null;
  private dataTypeInUseCheckPendingKey: string | null = null;
  private dataTypeInUseSubscription: Subscription | null = null;
  private keyUniqueUnchangedSnapshot: { key: string; organizationId: number | null; category: string } | null = null;
  private keyUniqueValidationTimer: ReturnType<typeof setTimeout> | null = null;

  /** Subscriptions tied to the current `editForm` instance (cleared on form rebuild or `ngOnDestroy`). */
  private formFieldSubscriptions = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private messageHandlerService: MessageHandlerService,
    private settingService: SettingService,
    private channelService: ChannelService,
    private translate: TranslateService,
    private organizationService: OrganizationService,
    private organizationValidators: OrganizationValidators,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private extensionService: ExtensionService,
    private dictionaryService: DictionaryService,
    private readonly destroyRef: DestroyRef)
  { }

  public get isDataTypesConfigurable(): boolean
  {
    return this.category && ExtensionEditComponent.CONFIGURABLE_DATA_TYPE_CATEGORIES.includes(this.category.value);
  }

  /** Kendo default listHeight (200px) is too small for data type list rows. */
  public get extensionDataTypeListHeight(): number
  {
    return this.extensionDataTypes.length * 34 + 8;
  }

  public get dataTypeInfoTooltipKey(): string
  {
    if (!this.isDataTypesConfigurable)
    {
      return 'EXTENSION-DATA-TYPES-STRING-ONLY';
    }
    if (this.dataTypeFieldsLocked)
    {
      return 'EXTENSION-DATA-TYPES-IN-USE';
    }
    return null;
  }

  public get enabled(): UntypedFormControl
  {
    return this.editForm.get('enabled');
  }

  public get enabledNextAvailable(): UntypedFormControl
  {
    return this.editForm.get('enabledNextAvailable');
  }

  public get allOrganizations(): UntypedFormControl
  {
    return this.editForm.get('allOrganizations');
  }

  public get allChannels(): UntypedFormControl
  {
    return this.editForm.get('allChannels');
  }

  public get channels(): UntypedFormControl
  {
    return this.editForm.get('channels');
  }

  public get required(): UntypedFormControl
  {
    return this.editForm.get('required');
  }

  public get billableItem(): UntypedFormControl
  {
    return this.editForm.get('billableItem');
  }

  public get key(): UntypedFormControl
  {
    return this.editForm.get('key');
  }

  public get name(): UntypedFormControl
  {
    return this.editForm.get('name');
  }

  public get category(): UntypedFormControl
  {
    return this.editForm.get('category');
  }

  public get organization(): UntypedFormControl
  {
    return this.editForm.get('organization');
  }

  public get dataType(): UntypedFormControl
  {
    return this.editForm.get('dataType');
  }

  public get codeSetId(): UntypedFormControl
  {
    return this.editForm.get('codeSetId');
  }

  public get maxLength(): UntypedFormControl
  {
    return this.editForm.get('maxLength');
  }

  public get decimals(): UntypedFormControl
  {
    return this.editForm.get('decimals');
  }

  @Input() public set formModel(extension: Extension)
  {
    this.initModel(extension);
  }

  public ngOnInit(): void
  {
    this.extensionDataTypes = this.extensionService.extensionDataTypes;
    this.dictionaryService.getCachedCodeSets().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) =>
      {
        this.codeSets = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });

    this.settingService.getCachedSessionSettings().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
      }
    });

    this.organizationService.getCachedOrganizations().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) =>
      {
        this.organizations = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });

    this.translate.get(['NO-CHANNELS-SELECTED', 'CANNOT-CHANGE-SETTING-EXTENSION-CATEGORY']).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) =>
      {
        this.noChannelSelected = res['NO-CHANNELS-SELECTED'];
        this.extensionCategoryCannotBeChanged = res['CANNOT-CHANGE-SETTING-EXTENSION-CATEGORY'];
      }
    });
  }

  public ngOnDestroy(): void
  {
    this.formFieldSubscriptions.unsubscribe();
  }

  /**
   * Disable inactive items from drop-down.
   *
   * @param itemArgs Item object
   */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  /**
   * Save handler
   *
   * @param e Event
   */
  public onSave(e: Event): void
  {
    e.preventDefault();
    const objectToPersist = this.editForm.value;

    // When editing and saving, if Category was changed then check if there are current Community Services, Organizations, Cases or Patients/Care Teams using it and, if so, do not allow saving
    if (!this.isNew  && (this.loadedExtension.category !== objectToPersist.category))
    {
      // Check if in use
      this.extensionService.isExtensionByCategoryInUse(this.loadedExtension.id, this.loadedExtension.category).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (inUseResponse) =>
        {
          if (inUseResponse)
          {
            this.messageHandlerService.displayDirectMessage('error', this.extensionCategoryCannotBeChanged);
            this.tabstrip.selectTab(0);
            return;
          }
          else
          {
            this.performExtensionSave(objectToPersist);
          }
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    else
    {
      this.performExtensionSave(objectToPersist);
    }
  }

  public performExtensionSave(objectToPersist: any): void
  {
    if (this.dataTypeFieldsLocked && this.loadedExtension)
    {
      objectToPersist.dataType = this.loadedExtension.dataType ?? 'TEXT';
      objectToPersist.codeSetId = this.loadedExtension.codeSetId ?? null;
      objectToPersist.maxLength = this.loadedExtension.maxLength ?? null;
      objectToPersist.decimals = this.loadedExtension.decimals ?? null;
    }
    else if (!this.isDataTypesConfigurable)
    {
      objectToPersist.dataType = 'TEXT';
      objectToPersist.codeSetId = null;
      objectToPersist.maxLength = null;
      objectToPersist.decimals = null;
    }
    else if (objectToPersist.dataType !== 'DICTIONARY')
    {
      objectToPersist.codeSetId = null;
    }
    if (objectToPersist.dataType !== 'NUMBER')
    {
      objectToPersist.decimals = null;
    }
    if (!this.extensionService.isExtensionStringDataType(objectToPersist.dataType))
    {
      objectToPersist.maxLength = null;
    }
    else if (!this.extensionService.hasExtensionStringMaxLength(objectToPersist))
    {
      objectToPersist.maxLength = null;
    }

    if (this.allOrganizations.value) 
    {
      this.organization.setValue(null);
      this.required.setValue(false);
      if (this.category.value === 'COMMUNITY_SERVICE' || this.category.value === 'ORGANIZATION') 
      {
        objectToPersist.allOrganizations = this.allOrganizations.value
      }
    }

    if (!this.allOrganizations.value || this.category.value !== 'PATIENT') 
    {
      objectToPersist.enabledNextAvailable = null;
    }

    if (objectToPersist.enabledNextAvailable)
    {
      if (objectToPersist.allChannels) 
      {
        objectToPersist.channels = [];
      }
      else
      {
        const selectedChannelIds = this.getSelectedChannelIds();
        if (!objectToPersist.allChannels && selectedChannelIds.length === 0)
        {
          this.messageHandlerService.displayDirectMessage('error', this.noChannelSelected);
          this.tabstrip.selectTab(2);
          return;
        }

        const channels = [];
        if (selectedChannelIds.length > 0)
        {
          selectedChannelIds.forEach((channelId: number) =>
          {
            let currentId = null;
            let currentVersion = 0;
            if (this.loadedExtension.channels)
            {
              const existingChannel: any = this.loadedExtension.channels.find(x => x.channelId === channelId);
              if (existingChannel)
              {
                currentId = existingChannel.id;
                currentVersion = existingChannel.version;
              }
            }
            channels.push({
              id: currentId,
              version: currentVersion,
              channelId,
              settingExtensionId: this.loadedExtension.id !== undefined ? this.loadedExtension.id : 0
            });
          });
          objectToPersist.channels = channels;
        }
      }
    }
    else 
    {
      objectToPersist.allChannels = null;
      objectToPersist.channels = [];
    }

    objectToPersist.organization = this.organization.value ? this.organization.value : null;

    this.save.emit(objectToPersist);
    this.resetExtensionEditSession();
  }

  /**
   * Cancel handler
   *
   * @param e Event
   */
  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  public keyOrgChange(field: string): void
  {
    if (field === 'key' && !this.key.hasError('keyExists'))
    {
      if (this.organization.hasError('keyExists'))
      {
        this.organization.setErrors(null);
      }
      if (this.category.hasError('keyExists'))
      {
        this.category.setErrors(null);
      }
    }
    if (field === 'organization' && !this.organization.hasError('keyExists'))
    {
      if (this.key.hasError('keyExists'))
      {
        this.key.setErrors(null);
      }
      if (this.category.hasError('keyExists'))
      {
        this.category.setErrors(null);
      }
    }
    if (field === 'category' && !this.category.hasError('keyExists'))
    {
      if (this.organization.hasError('keyExists'))
      {
        this.organization.setErrors(null);
      }
      if (this.key.hasError('keyExists'))
      {
        this.key.setErrors(null);
      }
    }
  }

  public validateKey(e: KeyboardEvent): void
  {
    TextProcessor.validateKeyWithUnderscoreAndDashNoSpaces(e);
    this.key.setValue(this.key.value.replaceAll(/ /g, ''));   // To handle paste that contains spaces
  }

  public isOrganizationRequired(): boolean
  {
    return this.editForm && !this.allOrganizations.value;
  }

  /**
   * Filters the channels based on user input.
   */
  public filterChannels(filter: string): void
  {
    this.searchTerms = filter;
    this.channelsFiltered = this.allSelectableChannels
      .filter(channel => channel.name.toLowerCase().includes(filter.toLowerCase()));
    this.setGroupedChannels();
  }

  public channelItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return itemArgs.dataItem.status !== 'Online';
  }

  private getSelectedChannelIds(): number[]
  {
    const value = this.channels?.value;
    if (!Array.isArray(value))
    {
      return [];
    }

    return value
      .map((channel: any) => typeof channel === 'number' ? channel : channel?.id ?? channel?.channelId)
      .filter((channelId: number) => channelId != null);
  }

  private syncChannelsFormToPrimitiveIds(): void
  {
    if (!this.channels)
    {
      return;
    }

    const channelIds = this.getSelectedChannelIds();
    this.channels.setValue(channelIds, { emitEvent: false });
  }

  /**
   * Closing edit form
   */
  private closeEditForm(): void
  {
    this.resetExtensionEditSession();
    this.cancelling.emit();
  }

  private resetExtensionEditSession(): void
  {
    this.editFormActive = false;
    this.initializedExtensionId = null;
    this.dataTypeInUseCheckKey = null;
    this.dataTypeInUseCheckPendingKey = null;
    this.dataTypeInUseSubscription?.unsubscribe();
    this.dataTypeInUseSubscription = null;
    this.keyUniqueUnchangedSnapshot = null;
    if (this.keyUniqueValidationTimer != null)
    {
      clearTimeout(this.keyUniqueValidationTimer);
      this.keyUniqueValidationTimer = null;
    }
  }

  private scheduleKeyUniqueValidation(): void
  {
    if (this.keyUniqueValidationTimer != null)
    {
      clearTimeout(this.keyUniqueValidationTimer);
    }
    this.keyUniqueValidationTimer = setTimeout(() =>
    {
      this.keyUniqueValidationTimer = null;
      if (this.key)
      {
        this.key.updateValueAndValidity({ emitEvent: false });
      }
    }, 300);
  }

  /**
   * Model initilization
   *
   * @param extension Extension edit object.
   */
  private initModel(extension: Extension): void
  {
    if (extension === undefined)
    {
      return;
    }

    const extensionId = extension.id ?? 0;
    if (this.editForm && this.initializedExtensionId === extensionId)
    {
      return;
    }

    this.initializedExtensionId = extensionId;
    this.dataTypeInUseCheckKey = null;
    this.dataTypeInUseCheckPendingKey = null;
    this.dataTypeInUseSubscription?.unsubscribe();
    this.dataTypeInUseSubscription = null;
    this.formFieldSubscriptions.unsubscribe();
    this.formFieldSubscriptions = new Subscription();

    this.channelService.getCacheMinimalChannels('?dashboardEnrollmentOnly=true').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) =>
      {
        this.applySelectableChannels(data);
        const channelsSelection = this.buildChannelsSelectionFromExtension(extension);
        this.loadMissingSelectableChannels(channelsSelection).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () =>
          {
            this.continueExtensionFormInit(extension, channelsSelection);
          },
          error: (errorResponse) =>
          {
            this.messageHandlerService.displayError(errorResponse);
            this.continueExtensionFormInit(extension, channelsSelection);
          }
        });
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      }
    });
  }

  private applySelectableChannels(data: any[]): void
  {
    this.allSelectableChannels = data;
    this.allSelectableChannels.forEach(c =>
    {
      c.programName = c.program.name;
    });
    this.channelsFiltered = this.allSelectableChannels.sort((a, b) => (a.name.localeCompare(b.name)));
    this.setGroupedChannels();
  }

  private buildChannelsSelectionFromExtension(extension: Extension): number[]
  {
    const channelsSelection: number[] = [];
    if (extension.channels !== null && extension.channels !== undefined)
    {
      extension.channels.forEach((channel: any) =>
      {
        const channelId = channel?.channelId ?? channel?.id;
        if (channelId != null && !channelsSelection.includes(channelId))
        {
          channelsSelection.push(channelId);
        }
      });
    }
    return channelsSelection;
  }

  private loadMissingSelectableChannels(channelIds: number[]): Observable<void>
  {
    const missingIds = channelIds.filter(id => !this.allSelectableChannels.some(c => c.id === id));
    if (missingIds.length === 0)
    {
      return of(void 0);
    }

    return forkJoin(missingIds.map(id => this.channelService.getMinimalChannelById(id))).pipe(
      map((lookups) =>
      {
        lookups.forEach((lookupChannel) =>
        {
          if (lookupChannel?.length === 1)
          {
            const channel = lookupChannel[0];
            channel.programName = channel.program?.name ?? '';
            if (!this.allSelectableChannels.some(c => c.id === channel.id))
            {
              this.allSelectableChannels.push(channel);
            }
          }
        });
        this.channelsFiltered = this.allSelectableChannels.sort((a, b) => (a.name.localeCompare(b.name)));
        this.setGroupedChannels();
      })
    );
  }

  private continueExtensionFormInit(extension: Extension, channelsSelection: number[]): void
  {
    this.currentUser = this.authenticationService.currentUser;
    if (this.currentUser)
    {
      this.userService.getUserById(this.currentUser.id, false).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (user) =>
        {
          this.currentUserOrganization = user.organization;

          // Set form
          this.editForm = this.fb.group({
                id: [extension.id !== null ? extension.id : 0],
                key: [extension.key !== null ? extension.key : '', Validators.required],
                name: [extension.name !== null ? extension.name : '', Validators.required],
                enabled: [extension.enabled !== null ? extension.enabled : true, Validators.required],
                enabledNextAvailable: [extension.enabledNextAvailable !== null ? extension.enabledNextAvailable : null],
                allOrganizations: [extension.allOrganizations !== null ? extension.allOrganizations : false],
                allChannels: [extension.allChannels !== null ? extension.allChannels : false],
                channels: [{ value: channelsSelection, disabled: false }],
                required: [extension.required !== null ? extension.required : false, Validators.required],
                category: [extension.category !== '' ? extension.category : 'CARE_TEAM', Validators.required],
                organization: [extension.organization && extension.organization.id !== 0 ? extension.organization : (this.isNew ? this.currentUserOrganization : null)],
                billableItem: [extension.billableItem !== null ? extension.billableItem : false, Validators.required],
                dataType: [this.extensionService.normalizeExtensionDataType(extension.dataType ?? 'TEXT'), Validators.required],
                codeSetId: [extension.codeSetId ?? null],
                maxLength: [extension.maxLength ?? null],
                decimals: [extension.decimals ?? null]
              });

              this.keyUniqueUnchangedSnapshot = extension.id > 0 ? {
                key: extension.key,
                organizationId: extension.allOrganizations ? null : (extension.organization?.id ?? null),
                category: extension.category
              } : null;

              const keyAsyncOrganizationValidators = [(): Observable<ValidationErrors | null> =>
                this.organizationValidators.validateOrganizationKeyUnique(
                  this.key,
                  this.organization.value ? this.organization.value.id : null,
                  extension.id,
                  this.category.value,
                  this.keyUniqueUnchangedSnapshot)
              ];

              this.key.setAsyncValidators(keyAsyncOrganizationValidators);

              this.setControlsState();

              this.formFieldSubscriptions.add(this.editForm.get('allOrganizations').valueChanges.subscribe({
                next: () =>
                {
                  this.setControlsState();
                  this.scheduleKeyUniqueValidation();
                }
              }));
              this.formFieldSubscriptions.add(this.editForm.get('organization').valueChanges.subscribe({
                next: () => { this.scheduleKeyUniqueValidation(); }
              }));
              let lastCategoryForInUseCheck: string = this.category.value;
              this.formFieldSubscriptions.add(this.editForm.get('category').valueChanges.subscribe({
                next: (category: string) =>
                {
                  this.setControlsState();
                  this.scheduleKeyUniqueValidation();
                  if (category !== lastCategoryForInUseCheck)
                  {
                    lastCategoryForInUseCheck = category;
                    this.updateDataTypeFieldsLocked();
                  }
                }
              }));
              this.formFieldSubscriptions.add(this.editForm.get('dataType').valueChanges.subscribe({ next: () => { this.setDataTypeControlsState(); } }));

              this.syncChannelsFormToPrimitiveIds();
              this.loadedExtension = extension;
              this.updateDataTypeFieldsLocked();
              this.scheduleKeyUniqueValidation();
              this.editFormActive = true;
            }
          });
    }
  }

  private setControlsState(): void
  {
    if (!this.inValueChanges)   // To prevent recursivity
    {
      this.inValueChanges = true;
      if (this.category && this.category.value)
      {
        if (this.category.value === 'COMMUNITY_SERVICE' || this.category.value === 'ORGANIZATION')
        {
          this.allOrganizations.disable();
          this.allOrganizations.setValue(true, { emitEvent: false });
        }
        else
        {
          this.allOrganizations.enable();
        }
      }
      else
      {
        this.allOrganizations.disable();
        this.allOrganizations.setValue(false, { emitEvent: false });
      }

      if (this.allOrganizations.value)
      {
        this.organization.disable();
        this.organization.setValidators([]);
        this.organization.setValue(null, { emitEvent: false });
      }
      else
      {
        this.organization.enable();
        this.organization.setValidators([Validators.required]);
      }
      this.allOrganizations.updateValueAndValidity();
      this.organization.updateValueAndValidity();
      this.setDataTypeControlsState();
      this.inValueChanges = false;
    }
  }

  private setDataTypeControlsState(): void
  {
    if (!this.editForm || !this.dataType)
    {
      return;
    }

    if (!this.isDataTypesConfigurable)
    {
      this.dataType.setValue('TEXT', { emitEvent: false });
      this.dataType.disable({ emitEvent: false });
      this.codeSetId.setValue(null, { emitEvent: false });
      this.codeSetId.clearValidators();
      this.codeSetId.disable({ emitEvent: false });
      this.maxLength.setValue(null, { emitEvent: false });
      this.maxLength.disable({ emitEvent: false });
      this.decimals.setValue(null, { emitEvent: false });
      this.decimals.disable({ emitEvent: false });
    }
    else if (this.dataTypeFieldsLocked)
    {
      this.dataType.disable({ emitEvent: false });
      this.codeSetId.disable({ emitEvent: false });
      this.maxLength.disable({ emitEvent: false });
      this.decimals.disable({ emitEvent: false });
    }
    else
    {
      this.dataType.enable({ emitEvent: false });
      if (this.dataType.value === 'DICTIONARY')
      {
        this.codeSetId.enable({ emitEvent: false });
        this.codeSetId.setValidators([Validators.required]);
      }
      else
      {
        this.codeSetId.setValue(null, { emitEvent: false });
        this.codeSetId.clearValidators();
        this.codeSetId.disable({ emitEvent: false });
      }

      if (this.extensionService.isExtensionStringDataType(this.dataType.value))
      {
        this.maxLength.enable({ emitEvent: false });
      }
      else
      {
        this.maxLength.setValue(null, { emitEvent: false });
        this.maxLength.disable({ emitEvent: false });
      }

      if (this.dataType.value === 'NUMBER')
      {
        this.decimals.enable({ emitEvent: false });
      }
      else
      {
        this.decimals.setValue(null, { emitEvent: false });
        this.decimals.disable({ emitEvent: false });
      }
    }

    this.dataType.setValidators([Validators.required]);
    this.dataType.updateValueAndValidity({ emitEvent: false });
    this.codeSetId.updateValueAndValidity({ emitEvent: false });
  }

  private updateDataTypeFieldsLocked(): void
  {
    if (!this.editForm || this.isNew || !this.loadedExtension?.id)
    {
      this.dataTypeFieldsLocked = false;
      this.dataTypeInUseCheckKey = null;
      this.dataTypeInUseCheckPendingKey = null;
      this.setDataTypeControlsState();
      return;
    }

    const category = this.category?.value;
    if (!ExtensionEditComponent.CONFIGURABLE_DATA_TYPE_CATEGORIES.includes(category))
    {
      this.dataTypeFieldsLocked = false;
      this.dataTypeInUseCheckKey = null;
      this.dataTypeInUseCheckPendingKey = null;
      this.setDataTypeControlsState();
      return;
    }

    const requestKey = `${this.loadedExtension.id}:${category}`;
    if (requestKey === this.dataTypeInUseCheckKey)
    {
      this.setDataTypeControlsState();
      return;
    }

    if (requestKey === this.dataTypeInUseCheckPendingKey)
    {
      return;
    }

    this.dataTypeInUseCheckPendingKey = requestKey;
    this.dataTypeInUseSubscription?.unsubscribe();
    this.dataTypeInUseSubscription = this.extensionService.isExtensionByCategoryInUse(this.loadedExtension.id, category)
      .subscribe({
        next: (inUse) =>
        {
          if (requestKey !== this.dataTypeInUseCheckPendingKey)
          {
            return;
          }
          this.dataTypeInUseCheckKey = requestKey;
          this.dataTypeInUseCheckPendingKey = null;
          this.dataTypeFieldsLocked = inUse;
          this.setDataTypeControlsState();
        },
        error: (errorResponse) =>
        {
          if (requestKey === this.dataTypeInUseCheckPendingKey)
          {
            this.dataTypeInUseCheckPendingKey = null;
          }
          this.messageHandlerService.displayError(errorResponse);
          this.dataTypeFieldsLocked = false;
          this.setDataTypeControlsState();
        }
      });
    this.formFieldSubscriptions.add(this.dataTypeInUseSubscription);
  }

  private getChannels(): void
  {
    this.channelService.getCacheMinimalChannels().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) =>
      {
        this.allSelectableChannels = data;
        this.allSelectableChannels.forEach(c =>
        {
          c.programName = c.program.name;
        });
        this.channelsFiltered = this.allSelectableChannels.sort((a, b) => (a.name.localeCompare(b.name)));
        this.setGroupedChannels();
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      }
    });
  }

  private setGroupedChannels(): void
  {
    // Build the grouped Channels
    const groupedChannels: any = groupBy(this.channelsFiltered, [{ field: 'programName' }]);
    this.groupedChannels.forEach(d =>
    {
      const items = sortBy(d.items, ['name']);
      d.items = items;
    });
    this.groupedChannels = sortBy(groupedChannels, ['value']);
  }
}

